import { useCallback, useMemo, useState } from 'react'
import type { Chart } from 'klinecharts'
import useBacktestChartState from '../../bot/hooks/useBacktestChartState'
import useScrubProjection from '../../bot/hooks/useScrubProjection'
import BacktestChart from '../../bot/components/BacktestChart'
import { precisionFromPointSize } from '../../bot/adapters/symbolsFetcher'
import { formatDateLabel } from '../../utils/chartUtils'
import { entryTimeOf } from '../../bot/utils/orderPairing'
import { replayZoom } from '../../bot/replayZoom'
import { WorkbenchReplayBar } from './WorkbenchReplayBar'
import WalkthroughTradesPane from './WalkthroughTradesPane'
import useWalkthroughDrawingsOverlay from './useWalkthroughDrawingsOverlay'
import useWalkthroughIndicators from './useWalkthroughIndicators'
import useWalkthroughOrders from './useWalkthroughOrders'
import {
  buildsFormingBar,
  clampBarMs,
  DEFAULT_BAR_MS,
  WALKTHROUGH_ENGINE_SPEED,
} from './walkthroughPacing'
import type { WalkthroughRun } from './useWalkthroughSession'
import type { BotCode } from '../../bot/api'
import type { SavedDrawing, SymbolSliceState } from './workbenchState'

const DEFAULT_PRECISION = 5

/**
 * Bar widths of blank chart held right of the newest bar.
 *
 * The replay reveals its candles at the right edge, and with no run-off each one arrives
 * pressed against the price axis — the reader sees it only after it has already stopped
 * moving. A few bars of space give the eye somewhere to follow it into. Small on purpose: it
 * is taken off the left of the same window, so a big margin would cost real history.
 */
const REPLAY_RIGHT_PAD_BARS = 5

/** klinecharts' own DEFAULT_BAR_SPACE: the zoom for a canvas that never saved one. */
const DEFAULT_BAR_SPACE = 8

export interface WorkbenchWalkthroughProps {
  run: WalkthroughRun
  /** Serialized drawings, frozen when the mode was entered. */
  drawings: SavedDrawing[]
  /** Indicators to mirror onto the replay chart. */
  indicators: SymbolSliceState['indicators']
  /** The canvas's bar width when the mode was entered, px — the zoom to open at if the user never chose one. */
  canvasBarSpace?: number | null
  /**
   * The bot's current code, as the Backtest tab shows it — for the pane's Code tab. Display
   * only: whether the run still matches the code is `run.codeId`'s business, in the session hook.
   */
  currentCode?: BotCode | null
  /** Whether the bot has generated code at all; without it the Code tab shows its empty label. */
  hasCode?: boolean
}

/**
 * The Walkthrough mode's read-only replay: the generated code trading the period the user
 * drew on, with their own drawings revealing alongside the real orders.
 *
 * Everything about playback — tick streaming, windowed candle loading, viewport clamping,
 * cursor persistence — comes from useBacktestChartState. This component adds what the
 * Backtest tab has no use for: the drawings layer, the pips-only trade pane, the live order
 * lines, and a transport band docked at the foot of the chart that speaks in time per bar
 * rather than in engine speeds.
 *
 * Read-only comes structurally, not by disabling things: there is simply no drawing rail, no
 * tool state and no pointer handlers on this subtree.
 */
export default function WorkbenchWalkthrough({
  run,
  drawings,
  indicators,
  canvasBarSpace = null,
  currentCode = null,
  hasCode = false,
}: WorkbenchWalkthroughProps) {
  // The raw engine, so the drawings and indicators can be painted onto the same chart the
  // replay drives. BacktestChart's imperative handle covers replay only.
  const [chart, setChart] = useState<Chart | null>(null)

  // ---- Pacing --------------------------------------------------------------------------
  // One value: wall-clock milliseconds per bar, the same on every timeframe. The engine reads it
  // as a bar-interval override at every speed; whether the bar also forms from ticks is decided
  // by the dwell (buildsFormingBar), not by a separate control. Passed straight through on every
  // change of the slider — no local drag state and no throttle — because with the engine
  // crediting the time already waited, a change costs a timer re-arm and nothing else.
  const [barMs, setBarMs] = useState(DEFAULT_BAR_MS)
  const handleBarMsChange = useCallback((next: number) => setBarMs(clampBarMs(next)), [])

  const {
    candles,
    orders,
    isLoading,
    loadError,
    chartRef,
    maxAvailableTs,
    progressViewPosition,
    progressViewTs,
    isPlaying,
    isPlayDisabled,
    handlePlay,
    handlePause,
    handleReset,
    handleStep,
    handleVisibleRangeChange,
    handleChartUserNavigate,
    handleEquityTimeClick,
    handleShowTime,
  } = useBacktestChartState({
    btid: run.btid,
    symbol: run.symbol,
    initialTimeframe: run.timeframe,
    initialStatus: run.status,
    initialProgressBacktest: run.progressBacktest,
    // A run opened to be played starts from the top: a cursor parked on an earlier visit would
    // skip the very idea the verification just checked.
    initialProgressView: run.autoplay ? null : run.progressView,
    // The DISPLAY span, not the run's real range. The server pads the period with indicator
    // warm-up before the drawings and a long trail after them, and these two values are what
    // bounds candle loading, paging and the viewport — so nothing before the first drawing is
    // ever loaded, and the progress bar measures exactly what the user watches.
    rangeStartTs: run.displayStartTs,
    rangeEndTs: run.displayEndTs,
    // Controlled, not initial, and nominal: every timing decision comes from the bar-interval
    // override below, which the engine consults before its speed table. Controlling it keeps
    // the hook from seeding its own 10x.
    speed: WALKTHROUGH_ENGINE_SPEED,
    // Named even though it equals the range start: without it a completed run nobody has
    // watched yet would open on its fully-drawn end state, and a walkthrough exists to be watched.
    replayStartTs: run.displayStartTs,
    // Whether the bar forms from ticks is decided by the dwell, not by a separate control: slow
    // enough to watch a candle build, it builds; faster, it pops in whole. See walkthroughPacing.
    formingBarEnabled: buildsFormingBar(barMs),
    barIntervalMsOverride: barMs,
    // A RUN THE USER JUST WAITED FOR STARTS ITSELF. Preparing a walkthrough takes long enough to
    // look away from, and the reward for coming back should not be another button: the chart
    // appearing IS the answer to "is it ready?", so it plays.
    //
    // Only for a run this session had built (`reused` false) and that has never been watched —
    // or one the Auto-verify loop just accepted and opened, which the user waited for all the
    // same, in the chat (`autoplay`). Re-entering a walkthrough is the opposite intent — the user
    // came back to a cursor they parked, usually to look at one bar — and starting there would run
    // the replay away from whatever they came to see.
    autoPlayOnReady: run.autoplay || (!run.reused && run.progressView == null),
    // A WALKTHROUGH IS WATCHED, NOT SCANNED. Scrolling back to check a level, or zooming in on
    // the bar an entry fired on, is part of watching — so it neither stops the run nor gets
    // undone by it: the replay keeps the user's zoom, and hands them the viewport for as long as
    // they are looking away from the playhead. The run stops on Pause, on a scrub, on a trade
    // row, and at the end.
    pauseOnChartNavigation: false,
  })

  // The run's own trades, fetched once and numbered once. Two readers below — the pane's rows
  // and the chart's order tooltip — and they have to agree on both which trades exist and what
  // each one is called.
  const { orders: runOrders, tickets: orderTickets } = useWalkthroughOrders({
    btid: run.btid,
    rangeStartTs: run.rangeStartTs,
    rangeEndTs: run.rangeEndTs,
    displayStartTs: run.displayStartTs,
    displayEndTs: run.displayEndTs,
  })

  // Same window as the progress readout, so the scrubber's own zero and the bar's 0% are the
  // same instant — and so dragging to either end lands where the run actually starts and
  // stops rather than somewhere in the padding the transport cannot express.
  const scrub = useScrubProjection({
    rangeStartTs: run.displayStartTs,
    rangeEndTs: run.displayEndTs,
    maxAvailableTs,
    progressViewPosition,
    onScrubTime: handleEquityTimeClick,
  })

  // Same triple BacktestChart uses to detect a series change, so the drawings reconcile picks
  // up pagination without the chart having to tell us.
  const dataKey = candles.length
    ? `${candles[0].time}:${candles[candles.length - 1].time}:${candles.length}`
    : ''

  useWalkthroughDrawingsOverlay({
    chart,
    drawings,
    revealTs: progressViewTs,
    dataKey,
  })

  useWalkthroughIndicators(chart, indicators, run.indicatorHistory, run.displayStartTs)

  // NOTHING THE CODE DID BEFORE THE FIRST DRAWING IS DRAWN. The chart's candles already start
  // there, but its orders are fetched by event time, so a position opened during the warm-up and
  // closed inside the window would still bring its exit arrow — an exit with no entry, for a trade
  // the pane (useWalkthroughOrders) rightly does not list. Judged on the entry, as the pane is.
  const chartOrders = useMemo(
    () => orders.filter((o) => {
      const entry = entryTimeOf(o)
      return entry !== null && entry >= run.displayStartTs
    }),
    [orders, run.displayStartTs],
  )

  // THE ZOOM IS THE USER'S. The bar width they last zoomed a replay to, on any strategy — or,
  // until they have, the one the canvas they just left was at, so entering the mode does not
  // change the size of the candles under them. Read once: from here on the chart holds it, and
  // only their own wheel or pinch moves it (and is remembered). See bot/replayZoom.
  const [barSpace] = useState(() => replayZoom.get() ?? canvasBarSpace ?? DEFAULT_BAR_SPACE)

  const bc = run.configInput?.backtest_config ?? {}
  const pipSize = typeof bc.pip_size === 'number' ? bc.pip_size : 0.0001
  const precision =
    precisionFromPointSize(typeof bc.point_size === 'number' ? bc.point_size : null)
    ?? DEFAULT_PRECISION

  // Floating P/L on open trades is measured against the newest revealed bar — the same
  // boundary the chart draws to, so the pane and the chart never disagree.
  const markPrice = candles.length > 0 ? candles[candles.length - 1].close : null

  // THE BAND'S OWN TERMS: where the replay starts, and how many of the run's trades it has
  // revealed. Counted on entry events exactly as the trades pane counts its rows (a trade is
  // listed once its entry is behind the playhead, open or closed), so the two never disagree.
  // Not `orderTickets.size`: tickets dedupe by entry second.
  const openedEvents = useMemo(() => runOrders.filter((o) => o.eventType === 'opened'), [runOrders])
  const tradeTotal = openedEvents.length
  const tradesRevealed = useMemo(
    () => openedEvents.filter((o) => o.time <= progressViewTs).length,
    [openedEvents, progressViewTs],
  )
  const fromLabel = formatDateLabel(run.displayStartTs)

  // THE END OF THE RUN. The cursor has run out of data to reveal: the play loop parks it on the
  // range end when the window is exhausted (usePlayback), and a seek to 100% lands there too.
  // The same signal the trades pane reads, so the band and the pane convert together.
  const atEnd = isPlayDisabled

  const handleTogglePlay = useCallback(() => {
    if (isPlaying) {
      handlePause()
      return
    }
    if (isPlayDisabled) return
    handlePlay()
  }, [isPlaying, isPlayDisabled, handlePlay, handlePause])

  const replayBar = useMemo(
    () => ({
      playing: isPlaying,
      atEnd,
      // Play again from the top: a reset lands on the replay start and queues its own play.
      onReplay: handleReset,
      tradesRevealed,
      tradeTotal,
      fromLabel,
      barMs,
      onBarMsChange: handleBarMsChange,
      value: scrub.visibleCount,
      max: scrub.totalCandles,
      maxScrubIndex: scrub.maxScrubIndex,
      onScrub: scrub.handleScrub,
      onTogglePlay: handleTogglePlay,
      onStepBack: () => handleStep(-1),
      onStepForward: () => handleStep(1),
    }),
    [
      isPlaying, atEnd, handleReset, tradesRevealed, tradeTotal, fromLabel, barMs, handleBarMsChange,
      scrub.visibleCount, scrub.totalCandles, scrub.maxScrubIndex, scrub.handleScrub,
      handleTogglePlay, handleStep,
    ],
  )

  return (
    <div className="absolute inset-0 flex flex-col bg-card">
      <div className="relative flex-1 min-h-0">
        <BacktestChart
          ref={chartRef}
          candles={candles}
          orders={chartOrders}
          symbol={run.symbol}
          timeframe={run.timeframe}
          autoSetInitialVisibleRange={false}
          boundPanToData
          rightPadBars={REPLAY_RIGHT_PAD_BARS}
          fixedBarSpace={barSpace}
          onBarSpaceChange={replayZoom.set}
          maxAvailableTimestamp={maxAvailableTs ?? undefined}
          onVisibleRangeChange={handleVisibleRangeChange}
          onUserNavigate={handleChartUserNavigate}
          onChartReady={setChart}
          // No volume in a walkthrough, deliberately: the question here is "does the code
          // fire where I drew?", and a volume histogram answers a different one. The user's
          // own volume preference on the canvas is untouched.
          showVolume={false}
          liveOrderLines
          pipSize={pipSize}
          // So the card a marker opens names the trade the same way its row does.
          orderTickets={orderTickets}
        />

        {/* The mode frame: a whitish hairline round the chart surface, so it reads as a mode
            rather than as generic chart furniture. With no dim around it, the frame carries
            the "you are in a mode, Exit to leave" message on its own. The band below sits
            outside it, on its own top rule. */}
        <div className="pointer-events-none absolute inset-0 z-[100] border border-foreground/30" />

        {loadError && (
          <div className="absolute inset-x-0 top-24 z-[110] flex justify-center">
            <span className="rounded-md border border-border bg-popover px-3 py-1.5 text-caption text-danger shadow-popover">
              {loadError}
            </span>
          </div>
        )}
        {!loadError && isLoading && candles.length === 0 && (
          <div className="absolute inset-x-0 top-24 z-[110] flex justify-center">
            <span className="rounded-md border border-border bg-popover px-3 py-1.5 text-caption text-muted-foreground shadow-popover">
              Loading candles…
            </span>
          </div>
        )}
      </div>

      {/*
        THE BAND IS A ROW OF THIS COLUMN, NOT AN OVERLAY ON THE CHART — on every width.
        It sits in the space between the chart and the trades pane, overlapping neither, and
        there WAS no such space: the chart fills its `flex-1` box right down to the pane, with
        its own time axis on the last rows, which is exactly what the floating bar used to
        cover. The space has to be MADE, and the honest place to make it is here, in the
        element that owns how the height is divided. As a sibling row the band takes its own
        height, the chart flexes down by exactly that, and the axis stays fully visible with
        no dead gap either — the band IS the gap. The phone had it this way first; the desktop
        followed, so the transport is one thing rather than two that drift.

        Two things fall out of it, both wanted: the mode frame above never draws its bottom
        edge across the band, and a press on the band cannot bubble into the chart pane's own
        gesture handling.
      */}
      <WorkbenchReplayBar {...replayBar} />

      <WalkthroughTradesPane
        orders={runOrders}
        playheadTs={progressViewTs}
        markPrice={markPrice}
        pipSize={pipSize}
        precision={precision}
        // Scrolls the chart to the trade and leaves the playhead alone — the replay keeps its
        // place, and see handleShowTime for why moving the cursor would also rewrite the very
        // list the click came from.
        onShowTrade={handleShowTime}
        // The cursor has run out of data to reveal, so the run is over as far as the user is
        // concerned. Lands the pane on Closed rather than on an empty Open tab.
        atEnd={isPlayDisabled}
        currentCode={currentCode}
        hasCode={hasCode}
      />
    </div>
  )
}
